import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { subMonthsFromDate, toIsoUtc } from '@helpers/dateUtils';
import { ShipmentType } from '@api/queries/inventory';
import { TankFilterValues } from '@components/Common/EntryFilters/TankSelectorControlled';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';

// URL param keys
const PARAM_TERMINAL = 'terminal';
const PARAM_TANK = 'tank';
const PARAM_FROM = 'from';
const PARAM_TO = 'to';
const PARAM_BILLED = 'billed';
const PARAM_SHIPMENT_TYPE = 'shipmentType';

// Default start date: 1 month ago
function getDefaultStartDate(): string {
  return toIsoUtc(subMonthsFromDate(new Date(), 1));
}

export interface BillingFilters {
  terminalId: string | null;
  tankId: string | null;
  startDate: string | null;
  endDate: string | null;
  isBilled: boolean | null;
  shipmentType: ShipmentType | null;
}

export interface UseBillingFiltersResult {
  filters: BillingFilters;
  tankFilter: TankFilterValues;
  dateFilter: DateFilterValues;
  setTankFilter: (value: TankFilterValues) => void;
  setDateFilter: (value: DateFilterValues) => void;
  setBilledFilter: (value: boolean | null) => void;
  setShipmentTypeFilter: (value: ShipmentType | null) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage billing filters via URL search params.
 *
 * URL format: /billing?terminal=xxx&tank=xxx&from=2024-01-01&to=2024-01-31&billed=true&shipmentType=1
 */
export function useBillingFilters(): UseBillingFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<BillingFilters>(() => {
    const terminalId = searchParams.get(PARAM_TERMINAL);
    const tankId = searchParams.get(PARAM_TANK);
    const startDate = searchParams.get(PARAM_FROM) || getDefaultStartDate();
    const endDate = searchParams.get(PARAM_TO);
    const billedParam = searchParams.get(PARAM_BILLED);
    const shipmentTypeParam = searchParams.get(PARAM_SHIPMENT_TYPE);

    return {
      terminalId,
      tankId,
      startDate,
      endDate,
      isBilled: billedParam === 'true' ? true : billedParam === 'false' ? false : null,
      shipmentType: shipmentTypeParam !== null ? parseInt(shipmentTypeParam, 10) as ShipmentType : null,
    };
  }, [searchParams]);

  // Tank filter for the controlled component
  const tankFilter = useMemo<TankFilterValues>(() => ({
    terminalId: filters.terminalId,
    tankId: filters.tankId,
  }), [filters.terminalId, filters.tankId]);

  // Date filter for the controlled component
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.startDate, filters.endDate]);

  // Update tank filter in URL
  const setTankFilter = useCallback((value: TankFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.terminalId) {
        newParams.set(PARAM_TERMINAL, value.terminalId);
      } else {
        newParams.delete(PARAM_TERMINAL);
      }

      if (value.tankId) {
        newParams.set(PARAM_TANK, value.tankId);
      } else {
        newParams.delete(PARAM_TANK);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update date filter in URL
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      // Only store non-default start date
      if (value.startDate && value.startDate !== getDefaultStartDate()) {
        newParams.set(PARAM_FROM, value.startDate);
      } else if (!value.startDate) {
        newParams.delete(PARAM_FROM);
      }

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update billed filter in URL
  const setBilledFilter = useCallback((value: boolean | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value !== null) {
        newParams.set(PARAM_BILLED, value.toString());
      } else {
        newParams.delete(PARAM_BILLED);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update shipment type filter in URL
  const setShipmentTypeFilter = useCallback((value: ShipmentType | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value !== null) {
        newParams.set(PARAM_SHIPMENT_TYPE, value.toString());
      } else {
        newParams.delete(PARAM_SHIPMENT_TYPE);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    tankFilter,
    dateFilter,
    setTankFilter,
    setDateFilter,
    setBilledFilter,
    setShipmentTypeFilter,
    clearFilters,
  };
}

export default useBillingFilters;
